import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import StagedAction from "ai-assistant/staged-actions/staged-action";
import { StagedActionResolutionFailure } from "ai-assistant/staged-actions/staged-action-resolution-failure";
import { StagedActionState } from "ai-assistant/staged-actions/statuses/staged-action-state";
import React from "react";

const ActionCard = styled(Card)(({ theme }) => ({
  borderColor: theme.palette.primary.light,
  borderStyle: "solid",
  borderWidth: "1px",
  marginTop: theme.spacing(1),
  maxWidth: "100%",
}));

const DetailList = styled("dl")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.5),
  margin: `${theme.spacing(1)} 0 0 0`,
}));

const DetailLabel = styled("dt")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
}));

const DetailValue = styled("dd")(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.caption.fontSize,
  overflowWrap: "anywhere",
}));

/**
 * What a card that can no longer be answered says instead of offering buttons.
 *
 * Every state has its own sentence, including the two nobody would think to write. `Executing`
 * is a card whose confirmation the server accepted and has not finished - it reads as work in
 * progress rather than as done. `Unknown` is work that was attempted and whose outcome nobody
 * can vouch for, and it deliberately does not say "nothing happened": the write may have
 * landed, and sending the user off to try again would be how one approval becomes two.
 */
function resolvedText(action: StagedAction): string {
  switch (action.state) {
    case StagedActionState.Executing:
      return "Working on it...";
    case StagedActionState.Succeeded:
      return "Done.";
    case StagedActionState.Failed:
      return "This could not be completed. Nothing was changed.";
    case StagedActionState.Cancelled:
      return "Cancelled.";
    case StagedActionState.Superseded:
      return "Something newer replaced this.";
    case StagedActionState.Unknown:
      return "This was started and I could not confirm whether it finished. Please check before asking again.";
    case StagedActionState.Expired:
    case StagedActionState.Pending:
    default:
      /* Pending reaches here only when the client's own clock says the preview has expired. */
      return "This expired before it was approved.";
  }
}

export interface StagedActionCardProps {
  action: StagedAction;
  busy: boolean;
  /**
   * What became of an answer that never arrived, if one has been attempted.
   *
   * Absent until something fails. A failure the user cannot retry takes the controls off the
   * card, so this is what stands between a confirm of unknown outcome and a second click on it.
   */
  failure?: StagedActionResolutionFailure;
  onConfirm: (stagedActionId: string) => void;
  onCancel: (stagedActionId: string) => void;
}

/**
 * What the assistant has prepared, and the only place it can be approved.
 *
 * Nothing has been written when this renders. The summary and details come from the server,
 * built from the arguments it validated - so the user is approving the work rather than the
 * model's description of it.
 *
 * A card that cannot be answered offers no buttons at all rather than a disabled one: a
 * control that looks clickable and always refuses is worse than no control.
 *
 * The forwarded ref is how the panel sends focus here when a turn has just staged a decision -
 * see `decisionToFocus` in `ai-assistant.tsx`. It is a ref and not a prop of its own because
 * a ref-typed *prop* makes the React Compiler read every access of `props` in this component
 * as a ref read during render and warn on all of them.
 */
const StagedActionCard = React.forwardRef<HTMLDivElement, StagedActionCardProps>(
  function StagedActionCard(props, forwardedRef) {
    const action = props.action;
    const stagedActionId = action.id.value;

    const handleConfirm = React.useCallback(
      () => props.onConfirm(stagedActionId),
      [props, stagedActionId]
    );
    const handleCancel = React.useCallback(
      () => props.onCancel(stagedActionId),
      [props, stagedActionId]
    );

    const failure = props.failure;
    /**
     * Names the card by the summary already on it, so that focus arriving here announces what is
     * being approved rather than "group". Pointing at the visible text rather than repeating it
     * in an `aria-label` keeps the two from drifting apart, and keeps one summary on the card.
     */
    const summaryId = React.useId();
    /*
     * Three independent blocks rather than one nested choice. A pending card that has just failed
     * shows the failure and, if answering again is safe, its buttons; it must never fall through
     * to `resolvedText`, which would report a live preview as expired.
     */
    const showsFailure = action.isPending && failure !== undefined;
    const showsControls = action.isPending && (failure === undefined || failure.canRetry);
    const showsOutcome = !action.isPending;

    return (
      <ActionCard
        ref={forwardedRef}
        elevation={0}
        variant="outlined"
        role="group"
        aria-labelledby={summaryId}
        /* Focusable so the panel can send focus here, never in the tab cycle itself. */
        tabIndex={-1}
      >
        <CardContent sx={{ pb: 1 }}>
          <Typography id={summaryId} variant="subtitle2" component="p">
            {action.summary}
          </Typography>
          {action.details.length > 0 && (
            <DetailList>
              {action.details.map((detail) => (
                <React.Fragment key={detail.label}>
                  <DetailLabel>{detail.label}</DetailLabel>
                  <DetailValue>{detail.value}</DetailValue>
                </React.Fragment>
              ))}
            </DetailList>
          )}
        </CardContent>
        {showsFailure && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" color="error" role="alert">
              {failure?.text}
            </Typography>
          </Box>
        )}
        {showsControls && (
          <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
            <Button size="small" onClick={handleCancel} disabled={props.busy}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleConfirm}
              disabled={props.busy}
              /* Named so a screen reader hears what is being approved, not just "Confirm". */
              aria-label={`Confirm: ${action.summary}`}
            >
              {props.busy ? <CircularProgress size={16} color="inherit" /> : "Confirm"}
            </Button>
          </CardActions>
        )}
        {showsOutcome && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            {/*
              `role="status"` because this replaces the buttons the user just pressed, and focus
              has gone back to the composer by the time it does. Without it, what became of an
              approved write is the one thing on the card a screen reader never says. Not an
              alert: a failed outcome is still an answer to something the user asked for, and
              interrupting is reserved for a resolution that never arrived at all - see the
              failure above.
            */}
            <Typography variant="caption" color="text.secondary" role="status">
              {resolvedText(action)}
            </Typography>
          </Box>
        )}
      </ActionCard>
    );
  });

export default StagedActionCard;
